import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  formatValue,
  createFormatDescriptor,
  type FormatContext,
} from '../runtime/formatting/index';
import { formatBsDate } from '../runtime/formatting/bikram-sambat';

dayjs.extend(customParseFormat);

export function formatDualDate(adDate: Date | string): string {
  const date = typeof adDate === 'string' ? dayjs(adDate).toDate() : adDate;
  if (!dayjs(date).isValid()) return '';
  return formatValue(date, createFormatDescriptor('date'));
}

export function formatAdDate(adDate: Date | string, format = 'YYYY-MM-DD'): string {
  const date = typeof adDate === 'string' ? dayjs(adDate).toDate() : adDate;
  return dayjs(date).format(format);
}

export { formatBsDate };

function normalizeLocaleParam(locale?: string): FormatContext['locale'] {
  const lower = locale?.toLowerCase() ?? '';
  return lower.startsWith('ne') ? 'ne' : 'en';
}

export function formatCurrency(amount: number | string, currency = 'NPR', locale?: string): string {
  return formatValue(
    amount,
    createFormatDescriptor('currency', {
      currencyCode: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    locale ? { locale: normalizeLocaleParam(locale) } : {}
  );
}

export function formatNumber(value: number | string, decimals = 2, locale?: string): string {
  return formatValue(
    value,
    createFormatDescriptor('decimal', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }),
    locale ? { locale: normalizeLocaleParam(locale) } : {}
  );
}

export function formatQuantity(qty: number | string, locale?: string): string {
  return formatValue(
    qty,
    createFormatDescriptor('quantity'),
    locale ? { locale: normalizeLocaleParam(locale) } : {}
  );
}

export function formatPercentage(value: number | string, decimals = 2, locale?: string): string {
  return formatValue(
    value,
    createFormatDescriptor('percentage', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }),
    locale ? { locale: normalizeLocaleParam(locale) } : {}
  );
}

export function parseNumber(value: string): number {
  return parseFloat(value.replace(/[,\s]/g, '')) || 0;
}

export function formatCompactNumber(num: number): string {
  if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toString();
}

export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? dayjs(date).toDate() : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}
